import type { SidebarDocumentPreview, SidebarFolderNode } from "@workith/shared";
import { Button, cn } from "@workith/ui";
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Folder,
  FolderOpen,
  Link as LinkIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

interface SidebarFolderTreeProps {
  folders: SidebarFolderNode[];
  selectedFolderId: string | null;
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  onSelectFolder: (id: string) => void;
  depth?: number;
}

function copyLink(path: string) {
  void navigator.clipboard.writeText(`${window.location.origin}${path}`);
}

function getSidebarDepthOffset(depth: number) {
  return Math.min(depth, 6) * 12;
}

export function SidebarDocumentPreviewList({
  documents,
  depth = 0,
}: {
  documents: SidebarDocumentPreview[];
  depth?: number;
}) {
  const navigate = useNavigate();

  if (documents.length === 0) {
    return null;
  }

  return (
    <div
      className="mt-1 space-y-0.5"
      style={{ marginLeft: `${getSidebarDepthOffset(depth) + 28}px` }}
    >
      {documents.map((document) => (
        <button
          key={document.id}
          type="button"
          onClick={() => navigate(`/editor/${document.id}`)}
          className="flex h-7 w-full min-w-0 items-center gap-2 rounded-md px-2 text-left text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          title={document.name}
        >
          <FileText className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{document.name}</span>
        </button>
      ))}
    </div>
  );
}

export function SidebarFolderTree({
  folders,
  selectedFolderId,
  expandedIds,
  onToggle,
  onSelectFolder,
  depth = 0,
}: SidebarFolderTreeProps) {
  return (
    <div className="space-y-0.5">
      {folders.map((folder) => {
        const isExpanded = expandedIds.has(folder.id);
        const isSelected = selectedFolderId === folder.id;
        const hasChildren = folder.children.length > 0;
        const Icon = isExpanded ? FolderOpen : Folder;
        const ToggleIcon = isExpanded ? ChevronDown : ChevronRight;

        return (
          <div key={folder.id}>
            <div
              className={cn(
                "group flex h-8 items-center gap-1 rounded-md pr-1 text-sm",
                isSelected
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-accent"
              )}
              style={{ paddingLeft: `${getSidebarDepthOffset(depth) + 4}px` }}
            >
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 p-0"
                onClick={() => onToggle(folder.id)}
                title={isExpanded ? "접기" : "펼치기"}
              >
                {hasChildren ? <ToggleIcon className="h-4 w-4" /> : <span className="h-4 w-4" />}
              </Button>
              <button
                type="button"
                onClick={() => onSelectFolder(folder.id)}
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
                title={folder.name}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{folder.name}</span>
              </button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  "h-6 w-6 shrink-0 p-0 opacity-0 group-hover:opacity-100",
                  isSelected && "text-primary-foreground"
                )}
                onClick={() => copyLink(`/dashboard?folderId=${folder.id}`)}
                title="폴더 링크 복사"
              >
                <LinkIcon className="h-3.5 w-3.5" />
              </Button>
            </div>
            {isSelected && (
              <SidebarDocumentPreviewList documents={folder.documents ?? []} depth={depth} />
            )}
            {isExpanded && folder.children.length > 0 && (
              <SidebarFolderTree
                folders={folder.children}
                selectedFolderId={selectedFolderId}
                expandedIds={expandedIds}
                onToggle={onToggle}
                onSelectFolder={onSelectFolder}
                depth={depth + 1}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
